import { MESApiService, spParameter } from "@dexteel/mesf-core";
import moment from "moment";
import type { PartInfo } from "../../../shared/models";
import type {
  PartDetailsResponse,
  PartsTreeResponse,
  PartsTreeRowAPI,
} from "../models";
import { PART_IDS, PART_TYPE_IDS } from "../models";

/**
 * Get the parts tree for EAF Roof tracking
 * Calls EQP.GetPartsTree stored procedure (Columbia base)
 *
 * @param rootPartId - Pass PART_IDS.EAF_ONLINE (1) to get all roofs and their panels
 */
export const getPartsTree = async (rootPartId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "RootPartId", value: rootPartId });
  parameters.push({ name: "Timestamp", value: null });

  const response = await apiService.callV2(
    "[RBD].[EQPGetPartsTree]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load parts tree",
    };
  }

  const data = response.data as PartsTreeResponse;

  return {
    ok: true,
    data,
  };
};

/**
 * Get details for a specific part (includes metrics and history)
 * Calls RBD.GetRoofPartDetails stored procedure
 *
 * Returns 3 result sets:
 * - [0] Master: PartName, PartTypeName, ParentPartName, InstalledOn, InstalledBy, ResetOn, ResetBy
 * - [1] Metrics: TotalValue, PartialValue, CampaignValue with status codes
 * - [2] History: Context-aware based on what's selected:
 *   - Roof: TOP 20 children tasks (all movements on that roof)
 *   - Location (ParentPartId + PositionId): Location history
 *   - Spare part: Part's movement history
 *
 * @param partId - The part to get details for (optional if using location mode)
 * @param parentPartId - Optional: Parent part ID for location history mode
 * @param positionId - Optional: Position ID for location history mode
 */
export const getPartDetails = async (
  partId: number | null,
  parentPartId?: number | null,
  positionId?: number | null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Timestamp", value: null });
  parameters.push({ name: "ParentPartId", value: parentPartId ?? null });
  parameters.push({ name: "PositionId", value: positionId ?? null });

  const response = await apiService.callV2(
    "[RBD].[EQPGetRoofPartDetails]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load part details",
    };
  }

  const data = response.data as PartDetailsResponse;

  // Process Master data dates (result set 0)
  data.tables[0].rows = data.tables[0].rows.map((row) => ({
    ...row,
    InstalledOn: row.InstalledOn ? moment.utc(row.InstalledOn).toDate() : null,
    ResetOn: row.ResetOn ? moment.utc(row.ResetOn).toDate() : null,
    UninstalledOn: row.UninstalledOn
      ? moment.utc(row.UninstalledOn).toDate()
      : null,
  }));

  // Process History data dates (result set 2)
  data.tables[2].rows = data.tables[2].rows.map((row) => ({
    ...row,
    Timestamp: row.Timestamp ? moment.utc(row.Timestamp).toDate() : null,
    PreviousTask: row.PreviousTask
      ? moment.utc(row.PreviousTask).toDate()
      : null,
  }));

  return {
    ok: true,
    data,
  };
};

/**
 * Set a roof online on the EAF.
 * Uses the Roof-specific wrapper for granular permission control.
 *
 * NOTE: Before calling this, you should setRoofOffline on the currently online roof
 * because EAF-Online can only hold ONE roof at a time (not an array)
 */
export const setRoofOnline = async (
  roofPartId: number,
  parentPartId: number,
  operatorNumber: number | null = null,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "RoofPartId", value: roofPartId });
  parameters.push({ name: "ParentPartId", value: parentPartId });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPRoofSetOnline]", parameters);
};

/**
 * Set a roof offline (moves to spares).
 * Uses the Roof-specific wrapper for granular permission control.
 */
export const setRoofOffline = async (
  roofPartId: number,
  operatorNumber: number | null = null,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "RoofPartId", value: roofPartId });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPRoofSetOffline]", parameters);
};

/**
 * Update part details (Make, Model, Vendor, SerialNumber, InstallDate, Comments)
 * Calls RBD.UpsertPartDetails stored procedure
 *
 * @param partId - The L2PartId (actual part instance)
 */
export const upsertRoofPartDetails = async (
  partId: number,
  partInfo: PartInfo,
  comments: string | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Make", value: partInfo.Make || null });
  parameters.push({ name: "Model", value: partInfo.Model || null });
  parameters.push({ name: "Vendor", value: partInfo.Vendor || null });
  parameters.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  parameters.push({ name: "Comments", value: comments || null });

  return await apiService.callV2("[RBD].[UpsertPartDetails]", parameters);
};

// movePart and createPart removed — replaced by EQPInstallPart (atomic)

/**
 * INSTALL PART - Unified: handles both new part creation and existing spare install
 *
 * Uses RBD.EQPInstallPart which wraps CreatePart + UpsertTask + UpsertPartDetails
 * in a transaction with controlled timestamps (prevents the create-after-install bug).
 *
 * @param roofPartId - The Roof PartId (L1PartId) - where to install
 * @param positionId - Position within the roof (1=Spider, 2=4th Hole Panel, etc.)
 * @param partTypeId - The specific panel type (PART_TYPE_IDS.SPIDER, etc.)
 * @param partInfo - Part details (Make, Model, Vendor, SerialNumber)
 * @param comments - Optional comments
 * @param operatorNumber - Optional operator number for audit
 */
export const installRoofPart = async (
  roofPartId: number,
  positionId: number,
  partTypeId: number,
  partInfo: PartInfo,
  comments: string | null = null,
  operatorNumber?: number,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "IsNewPart", value: true });
  parameters.push({ name: "PartTypeId", value: partTypeId });
  parameters.push({ name: "PartName", value: partInfo.SerialNumber || null });
  parameters.push({ name: "ParentPartId", value: roofPartId });
  parameters.push({ name: "PositionId", value: positionId });
  parameters.push({ name: "Make", value: partInfo.Make || null });
  parameters.push({ name: "Model", value: partInfo.Model || null });
  parameters.push({ name: "Vendor", value: partInfo.Vendor || null });
  parameters.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPInstallPart]", parameters);
};

/**
 * INSTALL EXISTING SPARE PART - Move an existing part from spares to a roof position
 *
 * Uses RBD.InstallPart with @IsNewPart = 0 (same SP as installRoofPart).
 *
 * @param existingPartId - The PartId of the spare part to install
 * @param roofPartId - The Roof PartId (L1PartId) - where to install
 * @param positionId - Position within the roof (1-12)
 */
export const installExistingRoofPart = async (
  existingPartId: number,
  roofPartId: number,
  positionId: number,
  comments: string | null = null,
  operatorNumber?: number,
  userId: number | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "IsNewPart", value: false });
  parameters.push({ name: "PartId", value: existingPartId });
  parameters.push({ name: "ParentPartId", value: roofPartId });
  parameters.push({ name: "PositionId", value: positionId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "UserId", value: userId });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPInstallPart]", parameters);
};

/**
 * UNINSTALL PART - Moves part to its type-specific spares container
 *
 * Uses RBD.EQPUninstallPart with chronological validation.
 *
 * @param partId - The L2PartId (actual installed part to uninstall)
 * @param operatorNumber - Optional operator number for audit
 */
export const uninstallRoofPart = async (
  partId: number,
  operatorNumber?: number,
  comments: string | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPUninstallPart]", parameters);
};

/**
 * REPAIR PART - Reset the partial metric
 *
 * Uses RBD.EQPRepairPart with chronological validation.
 *
 * @param partId - The L2PartId (actual installed part)
 * @param operatorNumber - Optional operator number for audit
 * @param comments - Optional comments for the task note
 */
export const repairRoofPart = async (
  partId: number,
  operatorNumber?: number,
  comments: string | null = null,
  timestamp: Date | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "Comments", value: comments || null });
  parameters.push({ name: "OperatorNumber", value: operatorNumber ?? null });
  parameters.push({
    name: "Timestamp",
    value: timestamp ? timestamp.toISOString() : null,
  });

  return await apiService.callV2("[RBD].[EQPRepairPart]", parameters);
};

// Mapping from spares container PartId to the part type that goes in it
// Uses unified types: 55°, 60°, and Skirt panels can be installed in either South or North positions
const SPARES_CONTAINER_TO_PART_TYPE: Record<number, number> = {
  [PART_IDS.SPIDER_SPARES]: PART_TYPE_IDS.SPIDER,
  [PART_IDS.FOURTH_HOLE_PANEL_SPARES]: PART_TYPE_IDS.FOURTH_HOLE_PANEL,
  [PART_IDS.FOURTH_HOLE_ELBOW_SPARES]: PART_TYPE_IDS.FOURTH_HOLE_ELBOW,
  [PART_IDS.FIFTY_FIVE_SPARES]: PART_TYPE_IDS.FIFTY_FIVE_DEGREE,
  [PART_IDS.SIXTY_SPARES]: PART_TYPE_IDS.SIXTY_DEGREE,
  [PART_IDS.SKIRT_SPARES]: PART_TYPE_IDS.SKIRT,
  [PART_IDS.DELTA_RING_SPARES]: PART_TYPE_IDS.DELTA_RING,
  [PART_IDS.CENTER_60_SPARES]: PART_TYPE_IDS.CENTER_60_DEGREE,
  [PART_IDS.ELBOW_SHIELD_SPARES]: PART_TYPE_IDS.ELBOW_SHIELD,
};

/**
 * CREATE SPARE PART - Create a new spare part in a spares container
 *
 * Flow:
 * 1. Create new part with RBD.CreatePart (using the correct PartTypeId for the container)
 * 2. Set part details with RBD.UpsertPartDetails
 *
 * @param containerId - The spares container PartId (e.g., SPIDER_SPARES = 20)
 * @param partInfo - Part details (Make, Model, Vendor, SerialNumber)
 */
export const createSparePart = async (
  containerId: number,
  partInfo: PartInfo,
  userId: number | null = null,
) => {
  const apiService = new MESApiService();

  // Get the part type for this container
  const partTypeId = SPARES_CONTAINER_TO_PART_TYPE[containerId];
  if (!partTypeId) {
    return { ok: false, message: "Invalid container ID" };
  }

  // Step 1: Create part
  // PartName is auto-generated by SP if not provided
  const partName = partInfo.SerialNumber || null;

  const createParams: spParameter[] = [];
  createParams.push({ name: "PartTypeId", value: partTypeId });
  createParams.push({ name: "PartName", value: partName });
  createParams.push({ name: "UserId", value: userId });

  const createResponse = await apiService.callV2(
    "[RBD].[EQPCreatePart]",
    createParams,
  );

  if (!createResponse.ok) {
    return createResponse;
  }

  // Get the new PartId
  const newPartId = createResponse.data?.returnValue;

  if (!newPartId) {
    return { ok: false, message: "Failed to get new part ID" };
  }

  // Set part details
  const detailsParams: spParameter[] = [];
  detailsParams.push({ name: "PartId", value: newPartId });
  detailsParams.push({ name: "Make", value: partInfo.Make || null });
  detailsParams.push({ name: "Model", value: partInfo.Model || null });
  detailsParams.push({ name: "Vendor", value: partInfo.Vendor || null });
  detailsParams.push({
    name: "SerialNumber",
    value: partInfo.SerialNumber || null,
  });
  detailsParams.push({ name: "InstallDate", value: null });
  detailsParams.push({ name: "Comments", value: null });

  return await apiService.callV2("[RBD].[UpsertPartDetails]", detailsParams);
};

/**
 * GET TASK METADATA - Get metadata (notes + operator) for a task
 *
 * @param taskId - The TaskId to get metadata for
 */
export const getTaskMetadata = async (taskId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "TaskId", value: taskId });

  const response = await apiService.callV2(
    "[RBD].[EQPGetTaskMetadata]",
    parameters,
  );

  if (!response.ok) {
    return {
      ok: false,
      message: response.message || "Failed to load task metadata",
    };
  }

  // Process dates
  const data = response.data;
  if (data?.tables?.[0]?.rows) {
    data.tables[0].rows = data.tables[0].rows.map(
      (row: { CreatedAt: string | Date }) => ({
        ...row,
        CreatedAt: row.CreatedAt ? moment.utc(row.CreatedAt).toDate() : null,
      }),
    );
  }

  return {
    ok: true,
    data,
  };
};

/**
 * ADD TASK METADATA - Add metadata (note + operator) to a task
 *
 * @param taskId - The TaskId to add metadata to
 * @param notes - The note text
 * @param userId - The userId of the current user
 */
export const addTaskMetadata = async (
  taskId: number,
  notes: string,
  userId: number | null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "TaskId", value: taskId });
  parameters.push({ name: "Notes", value: notes });
  parameters.push({ name: "UserId", value: userId });

  return await apiService.callV2("[RBD].[EQPAddTaskMetadata]", parameters);
};

/**
 * DELETE SPARE PART - Soft delete a spare part (sets IsActive = 0)
 *
 * The part must be in a spares container (not installed on a roof).
 * History is preserved for audit purposes.
 *
 * @param partId - The L2PartId of the spare part to delete
 * @param userId - The userId of the current user (for audit)
 */
export const deleteSparePart = async (
  partId: number,
  userId: number | null = null,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [];
  parameters.push({ name: "PartId", value: partId });
  parameters.push({ name: "UserId", value: userId });

  return await apiService.callV2("[RBD].[DeleteSparePart]", parameters);
};
